import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Clock, Loader2, MapPin, CheckCircle, AlertCircle, Play, Pause, TrendingUp, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { LoadingSpinner } from '../common/Badges';

const TechnicianTimeTracking = () => {
  const [weeklyData, setWeeklyData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ job_id: '', hours: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [hoursData, jobsData] = await Promise.all([
        api('/api/technician/my-hours'),
        api('/api/service-requests')
      ]);
      setWeeklyData(hoursData);
      setJobs(jobsData.filter(j => ['assigned', 'in_progress'].includes(j.status)));
    } catch (err) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      await api('/api/time-entries', { method: 'POST', body: JSON.stringify({ ...formData, hours: parseFloat(formData.hours) }) });
      toast.success('Time entry added');
      setFormData({ job_id: '', hours: '', description: '' });
      loadData();
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  if (loading) return <LoadingSpinner />;

  const weekStart = weeklyData?.week_start ? new Date(weeklyData.week_start).toLocaleDateString() : '';
  const weekEnd = weeklyData?.week_end ? new Date(weeklyData.week_end).toLocaleDateString() : '';
  const totalH = weeklyData?.total_hours || 0;
  const regularH = weeklyData?.regular_hours || 0;
  const overtimeH = weeklyData?.overtime_hours || 0;
  const daily = weeklyData?.daily_breakdown || {};
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const maxDayH = Math.max(...Object.values(daily), 8);

  return (
    <div data-testid="time-tracking" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Time Tracking</h1>
          <p className="text-gray-500 text-sm mt-1">Week: {weekStart} - {weekEnd}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-600 bg-gray-800 px-3 py-1 rounded">NYC Labor Standards</span>
          <span className="text-xs text-gray-600 bg-gray-800 px-3 py-1 rounded">OT after 40h/week = 1.5x</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white" data-testid="total-hours">{totalH.toFixed(1)}h</p>
              <p className="text-xs text-gray-500">Total Hours</p>
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-green-500/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white" data-testid="regular-hours">{regularH.toFixed(1)}h</p>
              <p className="text-xs text-gray-500">Regular (max 40h)</p>
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 ${overtimeH > 0 ? 'bg-orange-500/20' : 'bg-gray-800'} rounded-lg flex items-center justify-center`}>
              <TrendingUp className={`w-5 h-5 ${overtimeH > 0 ? 'text-orange-400' : 'text-gray-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-bold ${overtimeH > 0 ? 'text-orange-400' : 'text-white'}`} data-testid="overtime-hours">{overtimeH.toFixed(1)}h</p>
              <p className="text-xs text-gray-500">Overtime (1.5x rate)</p>
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{weeklyData?.total_sessions || 0}</p>
              <p className="text-xs text-gray-500">Sessions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Progress + Daily Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Progress */}
        <div className="card p-6">
          <h3 className="text-xs text-red-500 font-bold tracking-widest mb-4">WEEKLY PROGRESS</h3>
          <div className="relative">
            <div className="w-full bg-gray-800 h-6 rounded-full overflow-hidden">
              <div className="h-full flex">
                <div className="bg-green-500 h-full transition-all duration-700" style={{ width: `${Math.min((regularH / 40) * 100, 100)}%` }} />
                {overtimeH > 0 && <div className="bg-orange-500 h-full transition-all duration-700" style={{ width: `${(overtimeH / 40) * 100}%` }} />}
              </div>
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-600">
              <span>0h</span>
              <span className="text-gray-500 font-medium">40h standard</span>
              <span>{Math.max(totalH, 40).toFixed(0)}h</span>
            </div>
          </div>
          {overtimeH > 0 && (
            <div className="mt-4 bg-orange-900/20 border border-orange-800/30 rounded p-3">
              <p className="text-xs text-orange-400 font-medium">NYC Overtime Active</p>
              <p className="text-xs text-gray-500 mt-1">{overtimeH.toFixed(1)} hours at 1.5x rate (NY Labor Law Section 142-2.2)</p>
            </div>
          )}
        </div>

        {/* Daily Breakdown Chart */}
        <div className="card p-6">
          <h3 className="text-xs text-red-500 font-bold tracking-widest mb-4">DAILY BREAKDOWN</h3>
          <div className="flex items-end gap-2 h-32">
            {daysOfWeek.map(day => {
              const h = daily[day] || 0;
              const pct = maxDayH > 0 ? (h / maxDayH) * 100 : 0;
              return (
                <div key={day} className="flex-1 flex flex-col items-center gap-1">
                  <p className="text-xs text-gray-400 font-medium">{h > 0 ? `${h.toFixed(1)}h` : ''}</p>
                  <div className="w-full rounded-t" style={{ height: `${Math.max(pct, 2)}%`, background: h > 8 ? '#f97316' : h > 0 ? '#22c55e' : '#1f2937' }} />
                  <p className="text-[10px] text-gray-600">{day.slice(0, 3)}</p>
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-3 text-[10px] text-gray-600">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-green-500" />Normal</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-orange-500" />{'>'}8h/day</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Entries */}
        <div className="lg:col-span-2 card">
          <div className="p-4 border-b border-gray-800 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-white">This Week's Entries</h2>
            <span className="text-xs text-gray-600">{(weeklyData?.entries || []).length} entries</span>
          </div>
          <div className="divide-y divide-gray-800">
            {(weeklyData?.entries || []).map((entry) => (
              <div key={entry.id} className="p-4 flex items-center justify-between" data-testid={`time-entry-${entry.id}`}>
                <div>
                  <p className="font-medium text-white text-sm">{entry.description}</p>
                  <p className="text-xs text-gray-500">{new Date(entry.created_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                </div>
                <p className="font-bold text-lg text-white">{entry.hours}h</p>
              </div>
            ))}
            {(weeklyData?.entries || []).length === 0 && (
              <div className="p-8 text-center text-gray-500 text-sm">No time entries this week. Use Clock In on Dashboard or log time manually below.</div>
            )}
          </div>
        </div>

        {/* Log Time Form */}
        <form onSubmit={handleSubmit} className="card p-6 space-y-4 h-fit">
          <h3 className="font-heading text-lg font-bold text-white">Log Time</h3>
          <select className="input" value={formData.job_id} onChange={(e) => setFormData({ ...formData, job_id: e.target.value })} required data-testid="job-select">
            <option value="">Select Job</option>
            {jobs.map((job) => (<option key={job.id} value={job.id}>{job.ticket_number} - {job.service_type.replace('_', ' ')}</option>))}
          </select>
          <input type="number" step="0.5" min="0.5" className="input" placeholder="Hours" value={formData.hours} onChange={(e) => setFormData({ ...formData, hours: e.target.value })} required data-testid="hours-input" />
          <textarea className="input min-h-[80px]" placeholder="Description of work..." value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required data-testid="time-description-input" />
          <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="log-time-btn">{submitting && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}Log Time</button>
        </form>
      </div>
    </div>
  );
};

// ===================== ADMIN PAGES =====================


export default TechnicianTimeTracking;
